import { useEffect, useRef } from 'react';
import { MessageSquarePlus } from 'lucide-react';

import type { PendingQuote } from '../hooks/useQuoteComments';

/**
 * Roughly what the menu measures.
 *
 * Constants rather than a measure-then-place pass: the alternative is a frame
 * of the menu drawn in the wrong spot, or one drawn invisibly, and this menu is
 * a fixed size — one button with one label.
 */
const MENU_WIDTH = 126;
const MENU_HEIGHT = 38;
/** How far off the pointer it sits, so the passage under it stays readable. */
const POINTER_GAP = 8;

interface QuoteCommentMenuProps {
  pending: PendingQuote;
  onAdd: () => void;
  onDismiss: () => void;
}

/**
 * What a right-click on a selected passage opens.
 *
 * It offers one thing and does not compose anything. Writing happens in the
 * Notes tab, where the rest of the thread is; this exists because right-clicking
 * a sentence should not silently move the keyboard somewhere else — the reviewer
 * says "comment on this" first, and the surface answers by opening the tab.
 *
 * Dismissed by anything that means "no": a click elsewhere, Escape, or scrolling
 * the passage out from under it.
 */
export function QuoteCommentMenu({
  pending,
  onAdd,
  onDismiss,
}: QuoteCommentMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPointerDown = (pointerEvent: PointerEvent) => {
      const target = pointerEvent.target;
      if (target instanceof Node && menuRef.current?.contains(target)) return;
      onDismiss();
    };
    const onKeyDown = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === 'Escape') onDismiss();
    };

    // Capture, so a click that also does something else still closes this first.
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onDismiss, true);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onDismiss, true);
    };
  }, [onDismiss]);

  // Physical `left`/`top`, deliberately, where the rest of this surface is
  // written in logical properties: the coordinates come from the pointer, which
  // does not mirror under `dir="rtl"`.
  const isFlippedUp =
    pending.y + POINTER_GAP + MENU_HEIGHT > window.innerHeight;
  const left = Math.min(
    Math.max(pending.x - MENU_WIDTH / 2, POINTER_GAP),
    window.innerWidth - MENU_WIDTH - POINTER_GAP
  );
  const top = isFlippedUp
    ? pending.y - POINTER_GAP - MENU_HEIGHT
    : pending.y + POINTER_GAP;

  return (
    <div className="gr-qmenu" ref={menuRef} style={{ left, top }} role="menu">
      <button
        className="gr-qmenu-btn"
        type="button"
        role="menuitem"
        onClick={onAdd}
      >
        <MessageSquarePlus aria-hidden="true" />
        Add comment
      </button>
    </div>
  );
}
